"use client"

import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Pagination,
  Box,
  Button,
  Divider,
  Chip,
  Paper,
  Container,
  alpha,
  useTheme,
} from "@mui/material"
import { Add, Info, Redeem as GiftIcon, ArrowForward } from "@mui/icons-material"
import DetailDialog from "../../components/DetailDialog/DetailDialog"
import GiftDetails from "./GiftDetails"
import GiftEditor from "./GiftEditor"

// Custom styles
const styles = {
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
    pb: 1,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  addButton: {
    borderRadius: 4,
    px: 2,
    py: 0.75,
    boxShadow: 2,
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  giftCard: {
    borderRadius: 4,
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
    },
  },
  giftImage: {
    height: 180,
    objectFit: "cover",
    objectPosition: "center",
  },
  giftContent: {
    p: 2,
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  giftName: {
    fontWeight: "bold",
    mb: 0.5,
    lineHeight: 1.2,
  },
  giftDescription: {
    mb: 1,
    color: "text.secondary",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    fontSize: "0.875rem",
  },
  statusChip: {
    borderRadius: 2,
    fontWeight: "bold",
    py: 0.25,
    height: 24,
  },
  actionButton: {
    borderRadius: 4,
    textTransform: "none",
    fontWeight: "bold",
    py: 0.5,
  },
  pagination: {
    mt: 3,
    mb: 2,
    display: "flex",
    justifyContent: "center",
    "& .MuiPaginationItem-root": {
      borderRadius: 4,
    },
  },
  emptyState: {
    textAlign: "center",
    py: 4,
  },
}

const pageSize = 4

const Gifts = ({ id }) => {
  const theme = useTheme()
  const [gifts, setgifts] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [openDialog, setOpenDialog] = useState(false)
  const [openEditor, setOpenEditor] = useState(false)
  const [current, setCurrent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchgifts = async (page = 1) => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/v2/partner/${id}/gifts`)
      const data = await response.json()
      setgifts(data.data)
      setTotalPages(Math.ceil(data.data.length / pageSize))
    } catch (error) {
      console.error("Failed to fetch gifts:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchgifts(currentPage)
  }, [currentPage])

  const handlePageChange = (event, value) => {
    setCurrentPage(value)
  }

  const handleAddGift = () => {
    setCurrent({ id: 0 })
    setOpenEditor(true)
  }

  const handleViewDetails = (gift) => {
    const data = {
      name: gift.name,
      description: gift.description,
      quantity: gift.quantity,
      nbr_pts: gift.nbr_pts,
      type: gift.type,
      isActive: gift.isActive,
      images: gift.Media.map((media) => "/api/media/" + media.media_id),
    }
    setCurrent(data)
    setOpenDialog(true)
  }

  const handleUpdateGift = (gift) => {
    let url = null
    if (gift.Media.length > 0) {
      url = "/api/media/" + gift.Media[0].media_id
    }
    const data = {
      id: gift.id,
      name: gift.name,
      description: gift.description,
      quantity: gift.quantity,
      nbr_pts: gift.nbr_pts,
      type: gift.type,
      isActive: gift.isActive,
      url: url,
      images: gift.Media.map((media) => "/api/media/" + media.media_id),
    }
    setCurrent(data)
    setOpenEditor(true)
  }

  const handleCloseEditor = () => {
    setOpenEditor(false)
  }

  const handleSaveGift = () => {
    fetchgifts(currentPage)
    setOpenEditor(false)
  }

  if (!gifts && !isLoading) {
    return (
      <Paper sx={styles.emptyState}>
        <GiftIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          No Gifts Available
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          There are no gifts to display at the moment.
        </Typography>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddGift} sx={styles.addButton}>
          Add Your First Gift
        </Button>
      </Paper>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box sx={styles.pageHeader}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GiftIcon sx={{ fontSize: 24, color: "primary.main", mr: 1.5 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                Gift Gallery
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your rewards and incentives
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddGift}
            startIcon={<Add />}
            sx={styles.addButton}
            size="small"
          >
            Add New Gift
          </Button>
        </Box>

        <Grid container spacing={2}>
          {gifts.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((gift) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={gift.id}>
              <Card sx={styles.giftCard}>
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    image={"/api/media/" + gift?.Media[0].media_id}
                    alt={gift.name}
                    sx={styles.giftImage}
                  />
                  <Chip
                    label={gift.type.toUpperCase()}
                    size="small"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      fontWeight: "bold",
                      borderRadius: 1,
                      height: 20,
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>

                <CardContent sx={styles.giftContent}>
                  <Typography variant="h6" sx={styles.giftName}>
                    {gift.name}
                  </Typography>

                  <Typography variant="body2" sx={styles.giftDescription}>
                    {gift.description}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                      {gift.nbr_pts} Points
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Qty: {gift.quantity}
                    </Typography>
                  </Box>

                  <Chip
                    label={gift.isActive ? "Active" : "Inactive"}
                    color={gift.isActive ? "success" : "error"}
                    variant="filled"
                    size="small"
                    sx={{
                      ...styles.statusChip,
                      alignSelf: "flex-start",
                      mb: 1,
                    }}
                  />

                  <Box sx={{ mt: "auto" }}>
                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Button
                        color="primary"
                        variant="text"
                        onClick={() => handleViewDetails(gift)}
                        sx={styles.actionButton}
                        size="small"
                      >
                        <Info sx={{ mr: 0.5, fontSize: 16 }} />
                        Details
                      </Button>

                      <Button
                        color="primary"
                        variant="contained"
                        onClick={() => handleUpdateGift(gift)}
                        sx={styles.actionButton}
                        size="small"
                        endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            sx={styles.pagination}
            size="large"
            variant="outlined"
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Paper>

      <DetailDialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen={false}>
        <Box sx={{ p: 3 }}>
          <GiftDetails data={current} />
        </Box>
      </DetailDialog>

      <DetailDialog open={openEditor} title={"Gift Editor"} onClose={handleCloseEditor} fullScreen={false}>
        <Box sx={{ p: 2 }}>
          <GiftEditor data={current} idPartner={id} onCancel={handleCloseEditor} onSave={handleSaveGift} />
        </Box>
      </DetailDialog>
    </Container>
  )
}

Gifts.propTypes = {
  id: PropTypes.string.isRequired,
}

export default Gifts

